import React, { FC, useCallback, useMemo } from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import { LayoutStrategy } from '@oolio-group/domain';
import { useTranslation } from '@oolio-group/localization';
import { GRID_OPTIONS, getLayoutNameKey } from '../../common/constants';
import { useScale } from '../../hooks/useScale';
import {
  getDefaultRowsPerPage,
  getEffectiveColumns,
  getEffectiveRowsPerPage,
  getMaxSupportedColumns,
  getMaxSupportedRows,
} from '../../utils/layoutHelper';
import LayoutSettingsStyles from './LayoutSettings.styles';

interface PillRowProps {
  options: number[];
  selectedValue: number;
  isDisabled: (option: number) => boolean;
  onSelect: (option: number) => void;
  testIdPrefix: string;
}

const PillRow: FC<PillRowProps> = ({
  options,
  selectedValue,
  isDisabled,
  onSelect,
  testIdPrefix,
}) => {
  const styles = LayoutSettingsStyles();

  return (
    <View style={styles.pillRow}>
      {options.map(option => {
        const disabled = isDisabled(option);
        const selected = option === selectedValue;
        return (
          <TouchableOpacity
            key={`${testIdPrefix}-${option}`}
            testID={`${testIdPrefix}-${option}`}
            accessibilityRole="radio"
            accessibilityState={{ checked: selected, disabled }}
            disabled={disabled}
            onPress={() => onSelect(option)}
            style={[
              styles.pill,
              selected && styles.pillSelected,
              disabled && styles.pillDisabled,
            ]}
          >
            <Text
              style={[styles.pillText, selected && styles.pillTextSelected]}
            >
              {option}
            </Text>
          </TouchableOpacity>
        );
      })}
    </View>
  );
};

export interface GridSizePickerProps {
  strategy: LayoutStrategy;
  columns: number;
  rows: number;
  onChangeColumns: (columns: number) => void;
  onChangeRows: (rows: number) => void;
}

const GridSizePicker: FC<GridSizePickerProps> = ({
  strategy,
  columns,
  rows,
  onChangeColumns,
  onChangeRows,
}) => {
  const styles = LayoutSettingsStyles();
  const { translate } = useTranslation();
  const { scale } = useScale();

  const maxSupportedColumns = useMemo(
    () => getMaxSupportedColumns(scale),
    [scale],
  );

  const maxSupportedRows = useMemo(() => getMaxSupportedRows(scale), [scale]);

  const isRowOptionDisabled = useCallback(
    (option: number) => {
      return (
        option > maxSupportedRows ||
        (strategy !== LayoutStrategy.GRID &&
          option !== getDefaultRowsPerPage(strategy))
      );
    },
    [maxSupportedRows, strategy],
  );

  const isColumnOptionDisabled = useCallback(
    (option: number) => option > maxSupportedColumns,
    [maxSupportedColumns],
  );

  // Only GRID renders the configured row count, and even then never more than
  // the viewport fits. The preview must show what the KDS will actually draw,
  // not a `rowsOnScreen` stranded by an earlier layout or an oversized screen.
  const effectiveRows = getEffectiveRowsPerPage(
    strategy,
    rows,
    maxSupportedRows,
  );

  // Same reasoning as the rows above: the KDS never draws more columns than
  // the viewport fits, so neither the highlighted pill nor the preview may.
  const effectiveColumns = getEffectiveColumns(columns, maxSupportedColumns);

  const note = useMemo(() => {
    const columnNote = translate('kitchenDisplay.columnSupportNote', {
      maxColumns: maxSupportedColumns,
    });
    // Grid is the only strategy whose row pills can be capped by the viewport,
    // so it is the only one that needs the row-cap explanation.
    if (strategy === LayoutStrategy.GRID) {
      return `${columnNote} ${translate('kitchenDisplay.rowPerScreenNotes', {
        maxSupportedRows,
      })}`;
    }
    return `${columnNote} ${translate('kitchenDisplay.rowsIgnoredNote', {
      layout: translate(getLayoutNameKey(strategy)),
      rows: getDefaultRowsPerPage(strategy),
    })}`;
  }, [maxSupportedColumns, maxSupportedRows, strategy, translate]);

  return (
    <View style={styles.gridCard}>
      <View style={styles.gridControls}>
        <Text style={styles.pillLabel}>{translate('common.columns')}</Text>
        <PillRow
          options={GRID_OPTIONS}
          // Highlights what the KDS will draw, matching the row pills: a
          // persisted `columnsOnScreen` wider than the viewport leaves a
          // disabled pill looking selected.
          selectedValue={effectiveColumns}
          isDisabled={isColumnOptionDisabled}
          onSelect={onChangeColumns}
          testIdPrefix="grid-columns"
        />
        <Text style={styles.pillLabel}>{translate('common.rows')}</Text>
        <PillRow
          options={GRID_OPTIONS}
          // Highlights what the KDS will draw, not the raw persisted value —
          // otherwise a stranded `rowsOnScreen` leaves a disabled pill looking
          // selected while the only enabled option appears unselected.
          selectedValue={effectiveRows}
          isDisabled={isRowOptionDisabled}
          onSelect={onChangeRows}
          testIdPrefix="grid-rows"
        />
        <Text testID="grid-size-note" style={styles.note}>
          {note}
        </Text>
      </View>
      <View style={styles.livePreview}>
        <Text style={styles.pillLabel}>
          {translate('kitchenDisplay.livePreview')}
        </Text>
        <View style={styles.livePreviewGrid}>
          {Array.from({ length: effectiveRows }, (_, row) => (
            <View key={`preview-row-${row}`} style={styles.livePreviewRow}>
              {Array.from({ length: effectiveColumns }, (_, column) => (
                <View
                  key={`preview-tile-${column}`}
                  testID="live-preview-tile"
                  style={styles.livePreviewTile}
                />
              ))}
            </View>
          ))}
        </View>
      </View>
    </View>
  );
};

export default GridSizePicker;
